import { useCallback } from "react";
import { useNotify } from "react-admin";
import { useDropzone } from "react-dropzone";
import "react-dropzone/examples/theme.css";

interface ICsvFileUploadInput {
  setValues: (value: any) => void;
  csvFileType: string;
  isHaveNullableField?: boolean;
  showErrorForEmptyColumns?: boolean;
}

const CsvFileUploadInput = ({
  setValues,
  csvFileType,
  isHaveNullableField,
  showErrorForEmptyColumns,
}: ICsvFileUploadInput) => {
  const notify = useNotify();

  // Regex to split CSV rows correctly even when commas are inside quotes
  const csvSplitRegex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;

  const isValidColumnLength = (type: string, length: number) => {
    switch (type) {
      case "invoice":
        return length === 10 || length === 13;
      case "credit_note":
        return length === 10;
      case "student_subsidy_setting":
        return length === 8;
      default:
        return false;
    }
  };

  const isValidTotalRow = (type: string, length: number) => {
    if (type === "student_subsidy_setting") {
      return true; // No limit
    } else {
      return length <= 50; // Max 50 row
    }
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file: any) => {
        const reader = new FileReader();

        reader.onload = () => {
          //get the first row
          const labels: any = (reader.result as string).split("\r\n").shift();
          //get all row
          const items: any = (reader.result as string).split("\r\n");
          //split each column in row into array
          const label = labels.split(",");
          //remove first row
          items.splice(0, 1);

          let values: any = [];
          let cleanItems: any = [];

          // Validate header column count
          if (!isValidColumnLength(csvFileType, label.length)) {
            notify(
              "Your CSV is invalid, please use the new version of csv",
              "warning",
            );
            return;
          }

          // cleaning the items from empty string first column
          items.forEach((record: string) => {
            // split a string by commas but ignore commas within double-quotes
            const splitRecord = record.split(csvSplitRegex);

            // Ignore row if ALL columns are empty or whitespace
            const isAllColumnsEmpty = splitRecord.every(col => col.trim() === "");

            // only insert if not have empty string;
            if (!isAllColumnsEmpty) {
              cleanItems.push(record);
            }
          });

          if (!isValidTotalRow(csvFileType, cleanItems.length)) {
            notify("Please upload less than 50 records", "warning");
            return;
          }

          // Parse and validate each cleaned items
          cleanItems.forEach((record: string) => {
            // split a string by commas but ignore commas within double-quotes
            const splitedRecord = record.split(csvSplitRegex);

            if (!isValidColumnLength(csvFileType, splitedRecord.length)) {
              notify(
                `Record data and Column is not have matching length`,
                "warning",
              );
              return;
            }

            let dataToInsert: any = {};
            let hasEmptyColumn = false;

            // Map each column to its corresponding header
            label.forEach((labelItem: string, index: number) => {
              const value = splitedRecord[index] ?? "";
              if (value.trim() === "") {
                hasEmptyColumn = true;
              }

              dataToInsert[labelItem] = value.trim() === "" ? null : value;
            });

            // Check whether to insert this record based on conditions
            const isGoForInsert =
              showErrorForEmptyColumns ||
              !hasEmptyColumn ||
              isHaveNullableField;

            if (isGoForInsert) {
              // Set row status for error display or loading indication
              const isError = showErrorForEmptyColumns && hasEmptyColumn && !isHaveNullableField;
              dataToInsert.status = isError ? "failed" : "pending";
              dataToInsert.message = isError
                ? "Error: One or more columns are missing values"
                : "Loading...";

              values.push(dataToInsert);
            }
          });

          setValues(values);
        };
        reader.readAsText(file);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [csvFileType, showErrorForEmptyColumns, isHaveNullableField, setValues],
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: ".csv",
    onDrop,
  });

  return (
    <div {...getRootProps({ className: "dropzone" })}>
      <input {...getInputProps()} />
      <p>Drag 'n' drop a CSV file here, or click to select files</p>
    </div>
  );
};

export default CsvFileUploadInput;
